<template>
  <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
    <div class="rounded-t mb-0 px-4 py-4 border-b border-blueGray-100">
      <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">Daily welfare</h6>
      <h2 class="text-blueGray-700 text-xl font-semibold">每日福利上新提醒</h2>
    </div>

    <div v-if="loading" class="p-8 text-center text-blueGray-500">正在加载每日福利上新提醒统计…</div>
    <div v-else-if="errorMessage" class="p-8 text-center text-red-500">{{ errorMessage }}</div>
    <template v-else-if="dashboard">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 px-4 pt-4">
        <div v-for="item in comparisonItems" :key="item.key" class="rounded border border-blueGray-100 p-4">
          <div class="flex items-center justify-between mb-3">
            <span class="font-semibold text-blueGray-700">{{ item.date_label }}</span>
            <span v-if="item.key === 'today'" class="text-xs text-green-600">当前已落库累计</span>
          </div>
          <dl class="grid grid-cols-2 gap-y-2 text-sm">
            <div><dt class="text-blueGray-400">新增订阅</dt><dd class="font-semibold text-green-600">{{ item.new }}</dd></div>
            <div><dt class="text-blueGray-400">续订</dt><dd class="font-semibold text-violet-600">{{ item.old }}</dd></div>
            <div><dt class="text-blueGray-400">发送成功</dt><dd class="font-semibold text-blue-600">{{ item.send }}</dd></div>
            <div><dt class="text-blueGray-400">拒收</dt><dd class="font-semibold text-amber-600">{{ item.refuse }}</dd></div>
            <div><dt class="text-blueGray-400">推送访问 UV</dt><dd class="font-semibold text-blueGray-700">{{ item.push_visit_uv }}</dd></div>
            <div><dt class="text-blueGray-400">推送参与 UV</dt><dd class="font-semibold text-blueGray-700">{{ item.push_join_uv }}</dd></div>
          </dl>
        </div>
      </div>

      <div v-if="!hasData" class="p-8 text-center text-blueGray-400">暂无每日福利上新提醒统计数据</div>
      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4">
        <div>
          <h3 class="mb-3 text-sm font-semibold text-blueGray-600">近 14 天订阅与发送</h3>
          <div class="relative h-350-px"><canvas ref="subscriptionCanvas"></canvas></div>
        </div>
        <div>
          <h3 class="mb-3 text-sm font-semibold text-blueGray-600">近 14 天推送转化</h3>
          <div class="relative h-350-px"><canvas ref="conversionCanvas"></canvas></div>
        </div>
      </div>

      <div v-if="hasMpResult" class="px-4 pb-4 text-xs text-blueGray-400">
        公众号：成功 {{ mpSendTotal }}，失败 {{ mpFailedTotal }}
      </div>
    </template>
    <div v-else class="p-8 text-center text-blueGray-500">暂无每日福利上新提醒统计数据</div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import { luckdrawApi } from "@/api/luckdraw.js";
import { buildDailyWelfareTrends, normalizeDailyWelfareComparison } from "./dailyWelfareSubscriptionPresenter.js";

Chart.register(...registerables);

export default {
  data() {
    return {
      loading: true,
      errorMessage: "",
      dashboard: null,
      subscriptionChart: null,
      conversionChart: null,
    };
  },
  computed: {
    comparisonItems() {
      const comparison = (this.dashboard || {}).comparison || {};
      return [
        { key: "today", ...normalizeDailyWelfareComparison(comparison.today) },
        { key: "yesterday", ...normalizeDailyWelfareComparison(comparison.yesterday) },
        { key: "seven_days_ago", ...normalizeDailyWelfareComparison(comparison.seven_days_ago) },
      ];
    },
    items() {
      return (this.dashboard || {}).items || [];
    },
    hasData() {
      return this.items.some((item) => ["new", "old", "send", "refuse", "push_visit_uv", "push_join_uv"].some((field) => Number(item[field] || 0) > 0));
    },
    mpSendTotal() {
      return this.items.reduce((total, item) => total + Number(item.mp_send || 0), 0);
    },
    mpFailedTotal() {
      return this.items.reduce((total, item) => total + Number(item.mp_failed || 0), 0);
    },
    hasMpResult() {
      return this.mpSendTotal > 0 || this.mpFailedTotal > 0;
    },
  },
  async mounted() {
    await this.loadDashboard();
  },
  beforeUnmount() {
    this.destroyCharts();
  },
  methods: {
    destroyCharts() {
      if (this.subscriptionChart) {
        this.subscriptionChart.destroy();
        this.subscriptionChart = null;
      }
      if (this.conversionChart) {
        this.conversionChart.destroy();
        this.conversionChart = null;
      }
    },
    async loadDashboard() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const result = await luckdrawApi.getDailyWelfareSubDashboard({ days: 14 });
        if (result.sta !== 0 || !result.data) {
          throw new Error(result.msg || "加载失败");
        }
        this.dashboard = result.data;
      } catch (error) {
        this.errorMessage = error.message || "加载每日福利上新提醒统计失败";
      } finally {
        this.loading = false;
      }
      if (this.dashboard) {
        await this.$nextTick();
        this.renderCharts();
      }
    },
    renderCharts() {
      this.destroyCharts();
      if (!this.hasData || !this.$refs.subscriptionCanvas || !this.$refs.conversionCanvas) {
        return;
      }
      const trend = buildDailyWelfareTrends(this.items);
      const options = {
        maintainAspectRatio: false,
        responsive: true,
        plugins: { legend: { position: "bottom" } },
        scales: {
          x: { grid: { display: false } },
          y: { title: { display: true, text: "人数" }, ticks: { precision: 0 } },
        },
      };
      this.subscriptionChart = new Chart(this.$refs.subscriptionCanvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: trend.labels,
          datasets: [
            { label: "新增订阅", data: trend.subscription.newCount, backgroundColor: "#10b981" },
            { label: "续订", data: trend.subscription.oldCount, backgroundColor: "#8b5cf6" },
            { label: "发送成功", data: trend.subscription.sendCount, backgroundColor: "#3b82f6" },
            { label: "拒收", data: trend.subscription.refuseCount, backgroundColor: "#f59e0b" },
          ],
        },
        options,
      });
      this.conversionChart = new Chart(this.$refs.conversionCanvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: trend.labels,
          datasets: [
            { label: "推送访问 UV", data: trend.conversion.visitCount, backgroundColor: "#3b82f6" },
            { label: "推送参与 UV", data: trend.conversion.joinCount, backgroundColor: "#10b981" },
          ],
        },
        options,
      });
    },
  },
};
</script>
